import { Dropdown, Grid, List, Typography } from "antd";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import "../../assets/css/NotificationPanel.css";

import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AppContext } from "../../context/AppContext";
import { usePusher } from "../../context/PusherContext";
import useHttp from "../../hooks/useHttp";
import { useListViewStore } from "../../pages/listViewStore";
import { errorNotification, successNotification } from "../../pages/utils/CommonNotifications";
import { formatTimestampToRelativeTime, getNotificationIconClassName, NOTIFICATION_STATUS, NOTIFICATION_TYPE, parseNotificationContent } from "../../pages/utils/notificationUtils";
import { useNotificationsStore } from "../../stores/notificationsStore";
import HTag from "../custom/tagComponents/HTag";

const { useBreakpoint } = Grid;
const { Text } = Typography;

const NotificationPanel = ({ chatView = false }) => {
  const { fetchData, fetchDataByPagination } = useHttp();
  const [appProperties] = useContext(AppContext);
  const currentOsyncUserId = appProperties?.currentUser?.osyncUserId;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isMobileViewport = !useBreakpoint().md;
  const { resetFilters } = useListViewStore();
  const isNotificationUnread = useCallback((notification) => {
    if (!notification) return true;
    return notification.status === NOTIFICATION_STATUS.UNREAD.value;
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const notificationDropDownListRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const hasInitiallyLoadedRef = useRef(false);

  const notifications = useNotificationsStore((s) => s.notifications);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);

  const fetchUnreadCount = useNotificationsStore((s) => s.fetchUnreadCount);
  const fetchNotifications = useNotificationsStore((s) => s.fetchNotifications);
  const markAsRead = useNotificationsStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationsStore((s) => s.markAllAsRead);
  const onIncomingNotification = useNotificationsStore((s) => s.onIncomingNotification);
  const markNotificationsAsReadByIds = useNotificationsStore((s) => s.markNotificationsAsReadByIds);

  const cursor = useNotificationsStore((s) => s.cursor);
  const loading = useNotificationsStore((s) => s.loading);

  const cursorRef = useRef(null);
  const loadingRef = useRef(false);

  useEffect(() => {
    if (currentOsyncUserId) {
      fetchUnreadCount({ fetchData, appProperties, userId: currentOsyncUserId });
    }
  }, [currentOsyncUserId, fetchData, fetchUnreadCount]);

  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const { connect, subscribe, unSubscribe } = usePusher();

  useEffect(() => {
    if (!currentOsyncUserId) return;

    const setupNotificationSubscription = async () => {
      try {
        const connectionSuccess = await connect(false, true);
        if (!connectionSuccess) return;

        const handleNewNotification = (data) => {
          if (!data) return;

          try {
            let payload = JSON.parse(data?.data);

            // Handle bulk-read payload
            if (payload && Array.isArray(payload.notificationIds) && !payload?.notificationId) {
              if (payload?.notificationIds?.length > 0 && currentOsyncUserId) {
                markNotificationsAsReadByIds({ notificationIds: payload.notificationIds });
              }
              return;
            }

            const notificationsList = Array.isArray(payload) ? payload : [payload];

            if (notificationsList?.length === 0) return;

            for (const notification of notificationsList) {
              if (!notification?.notificationId) continue;

              const currentNotifications = useNotificationsStore.getState().notifications || [];
              const existingNotification = currentNotifications.find((n) => n.notificationId === notification.notificationId);
              const isNewNotification = !existingNotification;

              onIncomingNotification(notification, {
                addToList: true,
                incrementUnreadCount: isNewNotification,
              });
            }
          } catch (e) {
            console.error("Error handling notification event:", e);
          }
        };

        subscribe("NOTIFICATION_UPDATE", handleNewNotification, false, true);
      } catch (error) {
        console.error("Error setting up notification subscriptions:", error);
      }
    };

    setupNotificationSubscription();

    return () => {
      unSubscribe("NOTIFICATION_UPDATE");
    };
  }, [currentOsyncUserId, connect, subscribe, unSubscribe, onIncomingNotification, markNotificationsAsReadByIds]);

  useEffect(() => {
    if (!currentOsyncUserId || !isOpen) return;

    if (!hasInitiallyLoadedRef.current) {
      hasInitiallyLoadedRef.current = true;
      fetchNotifications({ fetchDataByPagination, appProperties, shouldRefresh: true });
    }
  }, [isOpen, currentOsyncUserId, fetchNotifications, fetchDataByPagination, appProperties]);

  const handleNotificationClick = useCallback(
    (notification) => {
      if (!notification) return;

      setIsOpen(false);

      if (chatView) {
        if (appProperties?.osyncId) {
          appProperties?.controller?.getUrlParamsForRedirection(true, appProperties).then(function (redirectionUrl) {
            if (notification.type === NOTIFICATION_TYPE.MISSED_CALL) {
              window.open("/voice/history?" + redirectionUrl, "_blank");
            } else {
              window.open("/inbox/" + notification?.associatedModuleDetails?.conversationId + "?" + redirectionUrl, "_blank");
            }
          });
        }
      } else if (notification?.type === NOTIFICATION_TYPE.MISSED_CALL) {
        navigate({ pathname: "/voice/history", search: location.search });
      } else {
        const contactId = notification?.associatedModuleDetails?.conversationId;
        if (contactId) {
          resetFilters();
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete("hs-inbox-view");
          const search = nextParams.toString();
          navigate(
            { pathname: `/inbox/${contactId}`, ...(search && { search }) },
            { replace: true, state: { fromNotification: true } }
          );
        }
      }

      markAsRead({ fetchData, appProperties, notification });
    },
    [chatView, markAsRead, fetchData, appProperties, navigate, location.search, searchParams, resetFilters],
  );

  const handleMarkAllAsRead = useCallback(() => {
    if (!currentOsyncUserId) return;

    markAllAsRead({ fetchData, appProperties, userId: currentOsyncUserId }).then(() => {
      successNotification("All notifications marked as read"); 
    }).catch((error) => {
      errorNotification("Error marking all notifications as read");
      console.error("Error marking all notifications as read:", error);
    });
  }, [currentOsyncUserId, markAllAsRead, fetchData, appProperties]);

  

  // Load more when sentinel at bottom becomes visible (viewport root works regardless of which element scrolls)
  useEffect(() => {
    if (!isOpen || !currentOsyncUserId || notifications?.length === 0) return;

    const sentinelEl = loadMoreSentinelRef.current;
    if (!sentinelEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;

        if (cursorRef.current === null || loadingRef.current) return;
        fetchNotifications({ fetchDataByPagination, appProperties, shouldRefresh: false });
      },
      {
        root: null,
        rootMargin: "0px 0px 80px 0px", // where to load more notifications 80px from the bottom of the viewport of sentinel element
        threshold: 0,
      },
    );

    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [isOpen, currentOsyncUserId, notifications?.length, fetchNotifications, fetchDataByPagination, appProperties]);

  const notificationContent = (
    <div
      className={`notification-panel-dropdown d-flex flex-column bg-white ${
        isMobileViewport	 ? "hs-w-100-vw hs-h-100vh rounded-0 border-0 shadow-none" : "hs-max-h-500-px hs-w-400-px rounded-2 border"
      }`}
    >
      <div
        className={`d-flex justify-content-between align-items-center notification-header hs-border-dark-grey-bottom p-3 ${isMobileViewport	 ? "px-4" : "px-3"}`}
      >
        <Typography.Text className={`${isMobileViewport	 ? "hs-fs-16 hs-fw-600" : "hs-fs-14 hs-fw-500"}`}>
          Notifications
        </Typography.Text>
        <div className="d-flex align-items-center gap-2">
          {unreadCount > 0 && (
            <span onClick={handleMarkAllAsRead} className="hs-color-violet text-decoration-underline cursor-pointer">
              Mark all as read
            </span>
          )}
          {isMobileViewport	 && (
            <Typography.Text
              onClick={() => setIsOpen(false)}
              className="notification-close-btn hs-fs-20 d-inline-flex align-items-center justify-content-center rounded cursor-pointer ms-2 hs-fs-16 hs-fw-600 hs-min-w-32-px hs-min-h-32-px"
            >
              ✕
            </Typography.Text>
          )}
        </div>
      </div>

      {loading && notifications?.length === 0 ? (
        <div className="d-flex align-items-center justify-content-center hs-min-h-200-px py-4">
          <div className="actionIconsSprite dropDownLoading" />
        </div>
      ) : notifications?.length === 0 ? (
        <div className="d-flex align-items-center justify-content-center p-5">
          <Typography.Text className="hs-color-dark-grey-2">No notifications</Typography.Text>
        </div>
      ) : (
        <div
          ref={notificationDropDownListRef}
          className="notification-list hs-max-h-500-px flex-grow-1 overflow-auto p-0 min-h-0 hs-default-list-scroll"
        >
          <List
            dataSource={notifications}
            rowKey="notificationId"
            renderItem={(item) => {
              const isUnread = isNotificationUnread(item);
              const { title, subtitle } = parseNotificationContent(item);

              return (
                <List.Item
                  onClick={() => handleNotificationClick(item)}
                  className={`d-flex align-items-start flex-nowrap notification-item gap-2 cursor-pointer hs-border-dark-grey-bottom ${isUnread ? "hs-bg-light-gray" : ""}`}
                >
                  <div className="flex-shrink-0 d-flex align-items-center justify-content-center notification-list-icon-container">
                    <div className={getNotificationIconClassName(item.type)} />
                  </div>
                  <div className="d-flex flex-column flex-grow-1 notification-text min-w-0 text-truncate me-3">
                    <div className="d-flex align-items-center justify-content-between min-w-0 gap-1">
                      <Text className={`hs-fs-13 hs-color-BlackRussian ${isUnread ? "hs-fw-600" : "hs-fw-400"}`} ellipsis>{title}</Text>
                    </div>
                    {subtitle && (
                      <Text ellipsis className="hs-fs-13 hs-fw-400 hs-color-dark-grey-2 d-block">{subtitle}</Text>
                    )}
                    <Text className="hs-fs-11 hs-fw-400 hs-color-md-grey">{formatTimestampToRelativeTime(item.createdAt)}</Text>
                  </div>
                  {isUnread && <div className="notification-unread-indicator rounded-circle flex-shrink-0 mt-1" />}
                </List.Item>
              );
            }}
          />
          <div ref={loadMoreSentinelRef} className="notification-load-more-sentinel pe-none" aria-hidden="true" />
          {loading && notifications?.length > 0 && (
            <div className="d-flex align-items-center justify-content-center">
              <div className="actionIconsSprite dropDownLoading position-absolute" aria-live="polite"  />
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => notificationContent}
      trigger={["click"]}
      placement={isMobileViewport	 ? "bottom" : "bottomRight"}
      open={isOpen}
      onOpenChange={setIsOpen}
      overlayClassName={isMobileViewport	 ? "notification-dropdown-mobile" : "notification-dropdown-desktop"}
    >
      <div className="position-relative cursor-pointer">
        <div className="actionIconsSprite-2 notificationIcon" aria-label="Notifications" role="button" />
        {unreadCount > 0 && (
          <div className="d-flex align-items-center justify-content-center notification-count-badge">
            <HTag count={unreadCount} maxDisplay={99} className="notification-count-badge-tag" />
          </div>
        )}
      </div>
    </Dropdown>
  );
};

export default NotificationPanel;
